import {
  Button,
  DatePicker,
  DatePickerProps,
  Modal,
  Segmented,
  Spin,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import {
  menteeObjectiveProps,
  MyObjectiveActions,
  USER_ROLES,
} from '../../types';
import { menteeObjectivescolumns } from '../mentor/mentee-objectives/columns';
import { Paths } from '../../routes';
import {
  getAllObjectives,
  updateObjectives,
} from '../../services/objectiveServices';
import { useMsal } from '@azure/msal-react';
import { Statuses, convertNameToPath } from '../../helper';
import dayjs from 'dayjs';
import ViewUserTooltip from '../../components/view-user-tooltip';
import { useAtomValue, useAtom } from 'jotai';
import { globalAtom, globalProps, userAtom } from '../../store/atom';
import { CreateObjective, NotificationAlert, Table } from '../../components';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { getUserPhoto } from '../../helper/config';
import UserProfile from '../../components/profile/UserProfile';

const MyObjective = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [userConf, setUserConf] = useState(false);
  const [data, setData] = useState<menteeObjectiveProps[]>([]);
  const [filtered, setFiltered] = useState<menteeObjectiveProps[]>([]);
  const { accounts } = useMsal();
  const { userInfo } = useAtomValue<globalProps>(globalAtom);
  const myRef = useRef<any>();
  const [editID, setEditID] = useState<null | string>(null);
  const [open, setOpen] = useState(false);
  const [callagain, setCallagain] = useState(true);
  const [modal, contextHolder] = Modal.useModal();
  const [profile, setProfile] = useState<string>('');
  const [_userState, setUserState] = useAtom(userAtom);

  // getting  and create user when user login first time
  useEffect(() => {
    if (accounts[0]) {
      setUserConf(true);
    }
  }, [accounts]);

  useEffect(() => {
    if (callagain) {
      (async () => {
        if (accounts[0].name) {
          setLoading(true);

          // Set selected mentee from global state
          if (userInfo) {
            //@ts-ignore
            setUserState((prev) => ({
              ...prev,
              selectedMentee: {
                ...prev.selectedMentee,
                id: accounts[0].localAccountId,
                name: accounts[0].name || '',
                designation: userInfo.designation,
                tier: userInfo.tier,
                mentor: userInfo.mentor,
              },
            }));
          }

          const { data, error } = await getAllObjectives(
            accounts[0].localAccountId
          );

          setCallagain(false);

          if (error) return myRef.current?.call('error', error?.message);
          if (!error) {
            const filter = data?.filter(
              (i) =>
                i.status.toLocaleLowerCase() !==
                Statuses.Delete.toLocaleLowerCase()
            );
            setData(filter);
            setFiltered(filter);
            setLoading(false);
          }
        }
      })();
    }
  }, [accounts[0], callagain, userInfo]);

  useEffect(() => {
    if (!userInfo) return setLoadingProfile(false);
    getUserPhoto(userInfo?.id)
      .then((blob) => {
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          setProfile(imageUrl);
        }
      })
      .finally(() => {
        setLoadingProfile(false);
      });
  }, []);

  // date change function
  const dateChange: DatePickerProps['onChange'] = (date) => {
    if (date === null) return setFiltered(data);
    if (data.length > 0) {
      const filter = data.filter(
        (i) =>
          dayjs(i.startDate).format('YYYY') === dayjs(date).format('YYYY') ||
          dayjs(i.endDate).format('YYYY') === dayjs(date).format('YYYY') ||
          (dayjs(date).isAfter(dayjs(i.startDate)) &&
            dayjs(date).isBefore(dayjs(i.endDate)))
      );
      setFiltered(filter);
    }
  };

  const handleNav = (val: string) => {
    if (val === 'Quarterly Feedback') {
      navigate(
        `${Paths.QuarterlyReview}/${convertNameToPath(accounts[0].name || '')}`,
        { state: { from: 'mentee' } }
      );
    }
  };

  if (!userConf)
    return (
      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    );

  const handleModal = (open: boolean) => {
    setOpen(open);
  };

  // delete objective
  const deleteObjective = async (
    data: menteeObjectiveProps,
    action: MyObjectiveActions
  ) => {
    const { error } = await updateObjectives(
      {
        ...data,
        status: action === 'delete' ? Statuses.Delete : Statuses.NotStarted,
        ...(action !== 'delete' && {
          startDate: dayjs().format('YYYY-MM-DD'),
        }),
      },
      data.id
    );
    if (error) return myRef.current?.call('error', error?.message);
    setCallagain(true);
    myRef.current?.call('success', 'Objective deleted successfully');
  };

  // action button click
  const handleClick = (
    data: menteeObjectiveProps,
    action: MyObjectiveActions
  ) => {
    if (action === 'edit') {
      setOpen(true);
      setEditID(data?.id);
    }
    if (action === 'delete') {
      modal.confirm({
        title: 'Are you sure you want to delete this objective?',
        icon: <ExclamationCircleFilled />,
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          return deleteObjective(data, action);
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-y-[10px]">
      <div className="flex py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">
          <ViewUserTooltip
            user={{
              name: accounts[0].name,
              mentor: userInfo?.mentor,
              designation: userInfo?.designation,
              tier: userInfo?.tier,
              profilePic: profile,
            }}
          />
        </div>
      </div>

      <div className="flex justify-end py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">Objectives Dashboard</div>
        <div className="ml-auto">
          <Segmented
            options={[
              'Objectives Dashboard',
              {
                label: 'Quarterly Feedback',
                value: 'Quarterly Feedback',
                disabled: false,
              },
            ]}
            defaultValue="Objectives Dashboard"
            onChange={handleNav}
          />
          <DatePicker
            onChange={dateChange}
            picker="year"
            className="ml-3 w-fit"
            placeholder="Year"
          />
        </div>
      </div>

      <div className="flex justify-end py-[10px] pl-[10px]">
        <div className="ml-auto">
          <Button
            type="primary"
            onClick={() => {
              setOpen(!open);
              setEditID(null);
            }}
          >
            Create Objective
          </Button>
        </div>
      </div>

      <Table
        columns={menteeObjectivescolumns(handleClick, USER_ROLES.MENTEE)}
        data={filtered}
        errorFetchingData={false}
        loading={loading}
        rowKey="id"
      />

      <CreateObjective
        open={open}
        setOpen={handleModal}
        editID={editID}
        setCallagain={setCallagain}
        from={USER_ROLES.MENTEE}
      />

      {loadingProfile ? (
        <> Loading...</>
      ) : (
        <UserProfile
          type="mentee"
          userId={userInfo?.id || ''}
          designation={userInfo?.designation || ''}
          tier={userInfo?.tier || ''}
        />
      )}

      <NotificationAlert ref={myRef} />
      {contextHolder}
    </div>
  );
};

export default MyObjective;
